import PropTypes from 'prop-types';
import React from 'react';
import { noop } from 'lodash';
import { t } from '../../../modules/infra/i18n';

import AcquisitionChannel from '../../main/customers/customerForm/containers/AcquisitionChannel';
import BasicFormField from '../formFields/basicFormField';
import PhoneFormField from '../formFields/phoneFormField';
import GenerateBarberShortLink from '../../main/barbers/containers/GenerateBarberShortLink';
import InstagramAuth from '../InstagramAuth';
import BarberUsername from '../../main/barbers/formBarbers/BarberUsername';
import PermissionPasscode from '../PermissionPasscode';
import ColorPickerField from '../uploadPhotoForm/colorPickerField';
import Tooltip from '../tooltip';
import LockableInput from '../LockableInput';
import { BRAND_ACCESS_MESSAGE } from '../constants';

const BasicInfo = ({
  customer,
  barber,
  barberId,
  instagramUserId,
  instagram,
  disabled,
  disableUsername,
  showUsername,
  userEnabled,
  onPopulateUsernameChange,
  isBarberUser,
  phoneFormatter,
  isBrandViewActive,
  canEditBarberName,
  canEditBarberPersonnelId,
  onEmailChange,
  leadAutoAssigned,
}) => (
  <div className="c-wrapper-horizontal">
    <div className="layout">
      <fieldset disabled={disabled}>
        <div className="layout__item u-1/2-medium-and-up">
          <div className="c-label-new">{t('common:basic_info.first_name_label')}</div>
          <LockableInput
            name="firstName"
            placeholder={t('common:basic_info.first_name_placeholder')}
            className="c-input fs-mask"
            type="text"
            disabled={barber && !canEditBarberName}
            hint={BRAND_ACCESS_MESSAGE}
          />
        </div>
        <div className="layout__item u-1/2-medium-and-up">
          <div className="c-label-new">{t('common:basic_info.last_name_label')}</div>
          <LockableInput
            name="lastName"
            placeholder={t('common:basic_info.last_name_placeholder')}
            className="c-input fs-mask"
            type="text"
            disabled={barber && !canEditBarberName}
            hint={BRAND_ACCESS_MESSAGE}
          />
        </div>
        <PhoneFormField
          disabled={disabled}
          label={t('common:basic_info.phone_label')}
          name="phone"
          placeholder={t('common:basic_info.phone_placeholder')}
          formatter={phoneFormatter}
        />
        <BasicFormField
          label={t('common:basic_info.email_label')}
          name="email"
          placeholder={t('common:basic_info.email_placeholder')}
          type="email"
          inputClass="fs-mask"
          onChange={onEmailChange}
        />
        <BasicFormField
          label={t('common:basic_info.instagram_label')}
          name="instagram"
          placeholder={t('common:basic_info.instagram_placeholder')}
          display-if={!customer && instagram && !isBrandViewActive}
          disabled={instagramUserId}
        />
        <InstagramAuth
          display-if={isBarberUser}
          disabled={disabled}
          owner="barber"
          id={barberId}
          wrapperClass="layout__item u-1/2-medium-and-up u-float-left"
        />
        <div className="layout__item u-1/2-medium-and-up" display-if={!customer}>
          <div className="c-label-new">{t('common:basic_info.personnel_id_label')}</div>
          <LockableInput
            name="personnelId"
            placeholder={t('common:basic_info.personnel_id_placeholder')}
            className="c-input"
            type="text"
            disabled={barber && !canEditBarberPersonnelId}
            hint={BRAND_ACCESS_MESSAGE}
          />
        </div>
        <ColorPickerField
          display-if={!customer}
          label={
            <>
              {t('common:basic_info.appointment_color_label')}
              <Tooltip className="u-type--no-transform c-info-tooltip-new--form c-info-tooltip-new--label">
                <div className="u-m--">
                  {t('common:basic_info.appointment_color_tooltip')}
                </div>
              </Tooltip>
            </>
          }
          name="color"
          wrapperClass="layout__item u-1/2"
        />
        <AcquisitionChannel leadAutoAssigned={leadAutoAssigned} className="layout__item u-1/2" display-if={customer} />
        <BarberUsername
          display-if={showUsername}
          disableUsername={disableUsername}
          userEnabled={userEnabled}
          onPopulateUsernameChange={onPopulateUsernameChange}
          isBarberUser={isBarberUser}
        />
        <GenerateBarberShortLink
          display-if={barber && barberId && !isBrandViewActive}
          barberId={barberId}
          wrapperClass="layout__item u-1/2-medium-and-up u-float-left"
        />
        <PermissionPasscode
          display-if={barber && barberId}
          barberId={barberId}
          wrapperClass="layout__item u-1/2-medium-and-up u-float-left"
        />
      </fieldset>
    </div>
  </div>
);

BasicInfo.propTypes = {
  customer: PropTypes.bool,
  barber: PropTypes.bool,
  barberId: PropTypes.string,
  instagramUserId: PropTypes.string,
  instagram: PropTypes.string,
  disabled: PropTypes.bool,
  disableUsername: PropTypes.bool,
  showUsername: PropTypes.bool,
  userEnabled: PropTypes.bool,
  onPopulateUsernameChange: PropTypes.func,
  isBarberUser: PropTypes.bool,
  phoneFormatter: PropTypes.func,
  isBrandViewActive: PropTypes.bool,
  canEditBarberName: PropTypes.bool,
  canEditBarberPersonnelId: PropTypes.bool,
  onEmailChange: PropTypes.func,
  leadAutoAssigned: PropTypes.bool,
};

BasicInfo.defaultProps = {
  customer: false,
  barber: false,
  barberId: '',
  instagramUserId: '',
  instagram: '',
  disabled: false,
  disableUsername: false,
  showUsername: false,
  userEnabled: false,
  onPopulateUsernameChange: noop,
  isBarberUser: false,
  phoneFormatter: (phone) => phone,
  isBrandViewActive: false,
  canEditBarberName: true,
  canEditBarberPersonnelId: true,
  onEmailChange: noop,
  leadAutoAssigned: false,
};

export default BasicInfo;